/**
 * Chiamate dei tag di strategia (SPEC-028). Nessun URL scritto nei componenti:
 * stanno qui.
 *
 * Il catalogo arriva dal backend e non e' duplicato qui: i tag, le loro
 * incompatibilita' e le regole d'uscita vivono in un posto solo
 * (`strategy-tags.catalog.ts`), e un'interfaccia con una copia propria
 * finirebbe per proporre un tag che il server rifiuta.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type StrategyTag =
  | 'short_term'
  | 'medium_term'
  | 'long_term'
  | 'earnings_play'
  | 'catalyst'
  | 'dip_buy'
  | 'momentum'
  | 'dividend_capture'
  | 'fundamental'
  | 'accumulation'
  | 'speculative';

export type StrategyParamKey = 'eventDate' | 'eventTiming' | 'eventLabel' | 'targetPrice' | 'stopPrice' | 'reviewBy' | 'note';

export type EventTiming = 'before_open' | 'during_session' | 'after_close' | 'unknown';

export type StrategyParams = Partial<{
  eventDate: string;
  eventTiming: EventTiming;
  eventLabel: string;
  targetPrice: string;
  stopPrice: string;
  reviewBy: string;
  note: string;
}>;

export type StrategyCatalogItem = {
  tag: StrategyTag;
  dimension: 'horizon' | 'thesis' | 'modifier';
  label: string;
  description: string;
  exitGuidance: string;
  params: StrategyParamKey[];
  conflictsWith: StrategyTag[];
};

export type ActiveStrategyTag = {
  id: string;
  tag: StrategyTag;
  params: StrategyParams;
  source: 'auto_trade' | 'agent' | 'manual' | string;
  reason: string | null;
  proposalId: string | null;
  agentRunId: string | null;
  appliedAt: string;
};

export type StrategyHistoryEntry = ActiveStrategyTag & {
  closedAt: string | null;
  closedReason: 'position_closed' | 'conflict' | 'replaced' | 'removed' | string | null;
  closedSource: string | null;
};

export type StrategyTagInput = { tag: StrategyTag; params: StrategyParams };

export const strategyKeys = {
  all: ['strategy-tags'] as const,
  catalog: ['strategy-tags', 'catalog'] as const,
  portfolio: (portfolioId: string) => ['strategy-tags', 'portfolio', portfolioId] as const,
  position: (portfolioId: string, instrumentId: string) => ['strategy-tags', 'position', portfolioId, instrumentId] as const,
};

const positionPath = (portfolioId: string, instrumentId: string) =>
  `/portfolios/${portfolioId}/instruments/${instrumentId}/strategy-tags`;

export function useStrategyCatalog() {
  return useQuery({
    queryKey: strategyKeys.catalog,
    queryFn: () => api.get<{ items: StrategyCatalogItem[] }>('/strategy-tags/catalog'),
    // Il catalogo cambia solo con un rilascio.
    staleTime: Infinity,
  });
}

/** I tag attivi di tutte le posizioni di un portafoglio, per strumento. */
export function usePortfolioStrategyTags(portfolioId: string) {
  return useQuery({
    queryKey: strategyKeys.portfolio(portfolioId),
    queryFn: () => api.get<{ items: Record<string, ActiveStrategyTag[]> }>(`/portfolios/${portfolioId}/strategy-tags`),
  });
}

export function usePositionStrategy(portfolioId: string, instrumentId: string) {
  return useQuery({
    queryKey: strategyKeys.position(portfolioId, instrumentId),
    queryFn: () =>
      api.get<{ active: ActiveStrategyTag[]; history: StrategyHistoryEntry[] }>(positionPath(portfolioId, instrumentId)),
  });
}

/** Salva l'insieme COMPLETO dei tag di una posizione. */
export function useSavePositionStrategy(portfolioId: string, instrumentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tags: StrategyTagInput[]) => api.put<{ active: ActiveStrategyTag[] }>(positionPath(portfolioId, instrumentId), { tags }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: strategyKeys.portfolio(portfolioId) });
      void queryClient.invalidateQueries({ queryKey: strategyKeys.position(portfolioId, instrumentId) });
    },
  });
}
